import { useState } from 'react';
import type { Customer } from '../services/customersApi';
import { useUpdateCustomer } from '../hooks/useCustomer';
import { useNotification } from '@/shared/hooks/useNotification';
import { useApiError } from '@/shared/hooks/useApiError';
import { useFieldErrorActions } from '@/shared/contexts/field-error-context';
import { useConfirm } from '@/shared/contexts/confirm-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Pencil } from 'lucide-react';
import { CustomerForm } from './CustomerForm';

interface EditCustomerDialogProps {
  customer: Customer;
}

export const EditCustomerDialog = ({ customer }: EditCustomerDialogProps) => {
  const formId = `editCustomer:${customer.id}`;
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    customerType: customer.customerType ?? 'Individual',
    customerName: customer.customerName,
    idNumber: customer.idNumber,
    mobileNo1: customer.mobileNo1,
    mobileNo2: customer.mobileNo2 || '',
    email: customer.email ?? '',
    county: customer.county,
    town: customer.town,
    road: customer.road,
    physicalLocationName: customer.physicalLocationName,
    gpsLocation: customer.gpsLocation,
    customerGroupId: customer.customerGroupId,
    creditLimit: customer.creditLimit,
    paymentTerms: customer.paymentTerms,
  });

  const updateMutation = useUpdateCustomer(customer.id);
  const notify = useNotification();
  const confirm = useConfirm();
  const handleApiError = useApiError();
  const { clearForm, clearField } = useFieldErrorActions();

  const handleChange = (
    field: keyof typeof formData,
    value: string | number
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearField(formId, field);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    clearForm(formId);

    confirm({
      title: 'Save customer changes?',
      description: `Changes to “${customer.customerName}” will be saved.`,
      confirmLabel: 'Save changes',
      onConfirm: () =>
        updateMutation
          .mutateAsync({ ...formData, isActive: customer.isActive })
          .then(() => {
            notify.success('Customer updated successfully');
            setOpen(false);
          })
          .catch((error) => {
            handleApiError(error, { formId });
            throw error;
          }),
    });
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) clearForm(formId);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground w-full justify-start">
          <Pencil className="w-4 h-4" />
          Edit
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 w-[95vw] sm:!max-w-[1400px]" >
        <DialogHeader>
          <DialogTitle>Edit Customer</DialogTitle>
          <DialogDescription>
            Update customer information
          </DialogDescription>
        </DialogHeader>

        <CustomerForm
          formId={formId}
          data={formData}
          onChange={handleChange}
          onSubmit={handleSubmit}
          isLoading={updateMutation.isPending}
          submitButtonText="Save Changes"
        />
      </DialogContent>
    </Dialog>
  );
};
